"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCartStore, selectItemQuantity } from "@/lib/cart/cart-store";
import { displayCurrency } from "@/lib/utils";
import { clientAPI } from "@/lib/client-fetch";
import API_ENDPOINTS from "@/constants/api-endpoints";
import AddToCartControls from "@/components/common/add-to-cart-controls";
import CaliiGuaranteeBanner from "@/components/common/calii-guarantee-banner";
import CategoryCarousel from "@/components/common/category-carousel";
import NavigationBreadcrumbs, { type Breadcrumb } from "@/components/common/navigation-breadcrumbs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SELECTION_UNIT_PIECE, SELECTION_UNIT_KG, KG_QUANTITY_STEP } from "@/constants/misc";

type SuggestedCategory = {
  id: number | string | null;
  title: string;
  items: any[];
};

type ProductDetailsViewProps = {
  product: any;
  initialProductId: string;
};

const MAX_BREADCRUMB_CHARACTERS = 18;

export default function ProductDetailsView({ product, initialProductId }: ProductDetailsViewProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [suggestedCategories, setSuggestedCategories] = useState<SuggestedCategory[]>([]);
  const [loadingSuggested, setLoadingSuggested] = useState(true);
  const cartEntries = useCartStore((s) => s.entries);

  useEffect(() => {
    const itemIdsCsv = cartEntries.map((e) => e.productId).join(",");
    setLoadingSuggested(true);
    clientAPI.get<{ suggested_collections?: SuggestedCategory[] }>(
      `${API_ENDPOINTS.SUGGESTED_ITEMS}?current_item_id=${initialProductId}&app_location=product_details&item_ids_csv=${itemIdsCsv}`
    ).then((result) => {
      if (result.ok) setSuggestedCategories(result.data?.suggested_collections ?? []);
      setLoadingSuggested(false);
    });
  }, [initialProductId]);
  const addItem = useCartStore((s) => s.addItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);

  if (!product) return null;
  
  const categoryBreadcrumbs: { id: string | number; name: string }[] = product.category_breadcrumbs ?? [];
  const breadcrumbs: Breadcrumb[] = categoryBreadcrumbs.map((crumb, idx) => ({
    label: crumb.name,
    href: idx === 0 ? `/categorias/${crumb.id}?prev=p` : `/categorias/${categoryBreadcrumbs[idx - 1].id}/${crumb.id}?prev=p`,
    maxCharacters: MAX_BREADCRUMB_CHARACTERS,
  }));
  breadcrumbs.push({ label: product.title, href: null, maxCharacters: MAX_BREADCRUMB_CHARACTERS });

  // Support both image_urls array and legacy image_url field
  const imageUrls: string[] = product.image_urls?.length > 0
    ? product.image_urls
    : [product.image_url];
  const selectedImageUrl = imageUrls[selectedImageIndex];
  const hasMultipleImages = imageUrls.length > 1;

  const goToPrevImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev === 0 ? imageUrls.length - 1 : prev - 1));
  }, [imageUrls.length]);

  const goToNextImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev === imageUrls.length - 1 ? 0 : prev + 1));
  }, [imageUrls.length]);

  // Keyboard navigation for gallery
  useEffect(() => {
    if (!galleryOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        goToPrevImage();
      } else if (e.key === "ArrowRight") {
        goToNextImage();
      } else if (e.key === "Escape") {
        setGalleryOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [galleryOpen, goToPrevImage, goToNextImage]);

  const productId = String(product.id);
  const selector = useMemo(() => selectItemQuantity(productId), [productId]);
  const quantity = useCartStore(selector);

  const handleAdd = () => {
    addItem(productId, product.default_selection_unit || SELECTION_UNIT_PIECE, product.measurement_unit_for_price || "", product.can_be_purchased_by_unit ?? true, product.can_be_purchased_by_kg ?? false, { displayPrice: product.display_price ?? null, pricePerPiece: product.price_per_piece ?? null, pricePerKg: product.price_per_kg ?? null, name: product.title, imageUrl: product.image_url });
  };

  const unit = product.default_selection_unit || SELECTION_UNIT_PIECE;
  const step = unit === SELECTION_UNIT_KG ? KG_QUANTITY_STEP : 1;

  const handleDecrement = () => {
    updateQuantity(productId, Math.round((quantity - step) * 10) / 10);
  };

  const handleIncrement = () => {
    updateQuantity(productId, Math.round((quantity + step) * 10) / 10);
  };

  const handleQuantityChange = (qty: number) => {
    updateQuantity(productId, qty <= 0 ? 0 : Math.round(qty * 10) / 10);
  };

  const priceMeasurementUnit =
    product.measurement_unit_for_price && product.measurement_unit_for_price.length > 0
      ? `/${product.measurement_unit_for_price}`
      : "";
  const displayPrice = product.display_price || product.price;
  const isOutOfStock = product.is_in_stock === false;
  const showUnit = true;//(product.can_be_purchased_by_unit && product.can_be_purchased_by_kg) || product.default_selection_unit === SELECTION_UNIT_KG;

  return (
    <>
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />

      <div className="px-4 md:px-16 xl:px-24 2xl:px-48 py-6 pb-24 md:pb-6">
        <div className="flex flex-col md:flex-row md:gap-10">
          {/* Image */}
          <div className="md:flex-1">
            <div
              className="relative w-full aspect-square max-h-[30vh] md:max-h-[75vh] 2xl:max-h-[60vh] max-w-md mx-auto md:max-w-none cursor-zoom-in"
              onClick={() => setGalleryOpen(true)}
            >
              <Image
                src={selectedImageUrl}
                alt={product.title}
                fill
                className="object-contain rounded-lg"
                sizes="(min-width: 768px) 50vw, 100vw"
                priority
              />
              {isOutOfStock && (
                <div className="absolute inset-0 bg-white/60 rounded-lg" />
              )}
              {isOutOfStock && (
                <div className="absolute top-3 left-3 rounded-md bg-gray-600 px-3 py-1 text-sm font-semibold text-white shadow-sm">
                  Agotado
                </div>
              )}
              {!isOutOfStock && !!product.percent_off && product.percent_off > 1 && (
                <div className="absolute top-3 left-3 rounded-md bg-red-500 px-3 py-1 text-sm font-semibold text-white shadow-sm">
                  {product.percent_off}% off
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {hasMultipleImages && (
              <div className="flex gap-2 mt-3 max-w-md mx-auto md:max-w-none overflow-x-auto">
                {imageUrls.map((url, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`relative w-16 h-16 cursor-pointer flex-shrink-0 rounded-md overflow-hidden border-2 transition ${
                      index === selectedImageIndex
                        ? "border-teal-400"
                        : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <Image
                      src={url}
                      alt={`${product.title} - ${index + 1}`}
                      fill
                      className="object-cover"
                      sizes="64px"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product details */}
          <div className="md:flex-1 mt-6 md:mt-0">
            {/* Title */}
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
              {product.title}
            </h1>

            {/* Subtitle */}
            {product.subtitle && (
              <p className="text-muted-foreground mb-4">{product.subtitle}</p>
            )}

            {/* Price */}
            <div className="mb-6">
              {!!product.percent_off ? (
                <div className="flex items-baseline gap-3">
                  <span className="text-base lg:text-lg text-red-600">
                    {displayCurrency(displayPrice)}
                    {priceMeasurementUnit}
                  </span>
                  <span className="text-base lg:text-lg text-muted-foreground line-through">
                    {displayCurrency(product.original_display_price)}
                    {priceMeasurementUnit}
                  </span>
                </div>
              ) : (
                <span className="text-base lg:text-lg text-muted-foreground">
                  {displayCurrency(displayPrice)}
                  {priceMeasurementUnit}
                </span>
              )}
            </div>

            {/* Calii Guarantee Badge */}
            <CaliiGuaranteeBanner />

            {/* Description */}
            {product.description && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-2">Descripción</h2>
                <p className="text-muted-foreground">{product.description}</p>
              </div>
            )}

            {/* Add to cart - desktop (inline) */}
            <div className="hidden md:block">
              {isOutOfStock ? (
                <Button
                  disabled
                  className="w-full bg-gray-400 text-white cursor-not-allowed"
                  size="lg"
                >
                  Agotado
                </Button>
              ) : (
                <AddToCartControls
                  quantity={quantity}
                  onAdd={handleAdd}
                  onDecrement={handleDecrement}
                  onIncrement={handleIncrement}
                  onQuantityChange={handleQuantityChange}
                  variant="full"
                  unit={showUnit ? product.default_selection_unit : undefined}
                  maxQuantity={unit === SELECTION_UNIT_KG ? (product.max_kg ?? null) : (product.max_units ?? null)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Suggested items */}
      {loadingSuggested && (
        <div className="px-4 md:px-8 xl:px-12 2xl:px-16 pb-8">
          <Skeleton className="h-4 w-32 mb-4" />
          <div className="flex gap-3 overflow-hidden">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-48 w-36 shrink-0 rounded-xl" />
            ))}
          </div>
        </div>
      )}
      {!loadingSuggested && suggestedCategories.length > 0 && (
        <div className="px-4 px-8 xl:px-12 2xl:px-16 pb-24">
          {suggestedCategories.map((category) => (
            <div key={category.title} className="pb-16">
              <CategoryCarousel
                title={category.title}
                products={category.items || []}
                seeAllHref={null}
              />
            </div>
          ))}
        </div>
      )}

      {/* Add to cart - mobile (sticky footer) */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg md:hidden">
        {isOutOfStock ? (
          <Button
            disabled
            className="w-full bg-gray-400 text-white cursor-not-allowed"
            size="lg"
          >
            Agotado
          </Button>
        ) : (
          <AddToCartControls
            quantity={quantity}
            onAdd={handleAdd}
            onDecrement={handleDecrement}
            onIncrement={handleIncrement}
            onQuantityChange={handleQuantityChange}
            variant="full"
            unit={showUnit ? product.default_selection_unit : undefined}
            maxQuantity={unit === SELECTION_UNIT_KG ? (product.max_kg ?? null) : (product.max_units ?? null)}
          />
        )}
      </div>

      {/* Image gallery lightbox */}
      {galleryOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 md:p-10"
          onClick={() => setGalleryOpen(false)}
        >
        <div
          className="relative flex flex-col bg-black/95 rounded-xl overflow-hidden w-full max-w-4xl h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => setGalleryOpen(false)}
            className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Image */}
          <div className="relative flex-1 min-h-0">
            <Image
              src={selectedImageUrl}
              alt={product.title}
              fill
              className="object-contain"
              sizes="100vw"
              priority
            />

            {/* Navigation arrows */}
            {hasMultipleImages && (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white"
                  onClick={goToPrevImage}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white"
                  onClick={goToNextImage}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {hasMultipleImages && (
            <div className="flex gap-2 p-4 justify-center overflow-x-auto shrink-0">
              {imageUrls.map((url, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`relative w-14 h-14 flex-shrink-0 rounded-md overflow-hidden border-2 transition ${
                    index === selectedImageIndex
                      ? "border-teal-400"
                      : "border-transparent hover:border-gray-500"
                  }`}
                >
                  <Image
                    src={url}
                    alt={`${product.title} - ${index + 1}`}
                    fill
                    className="object-cover"
                    sizes="56px"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
        </div>
      )}


    </>
  );
}
